import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router';
import { AgentProvider, useAgent } from '../lib/agent';
import { displayFirstName, initials } from '../lib/names';
import { useSession } from '../lib/session';
import { resetViewport, useVisualViewport } from '../lib/viewport';
import { BrandMark } from './ui/BrandMark';
import { Person, Tasks as TasksIcon } from './ui/icons';
import { Lockup } from './ui/Lockup';
import { VvDebug } from './VvDebug';

const AUTH_ROUTES = ['/signin', '/onboarding', '/invite'];

const bottomLink = ({ isActive }: { isActive: boolean }) =>
  [
    'flex min-h-12 flex-1 flex-col items-center justify-center gap-1 rounded-md text-xs font-medium',
    'transition-colors duration-[var(--pa-dur-fast)]',
    isActive ? 'text-ink' : 'text-ink-2 hover:text-ink',
  ].join(' ');

/** The 256px rail: icon and label side by side. */
const railLink = ({ isActive }: { isActive: boolean }) =>
  [
    'flex min-h-11 items-center gap-3 rounded-md px-3 text-sm font-medium',
    'transition-colors duration-[var(--pa-dur-fast)]',
    isActive ? 'bg-brand-soft text-ink' : 'text-ink-2 hover:bg-canvas hover:text-ink',
  ].join(' ');

/** The 72px compact rail (768 to 1023): icon with the label beneath. */
const compactLink = ({ isActive }: { isActive: boolean }) =>
  [
    'flex min-h-14 w-full flex-col items-center justify-center gap-1 rounded-md px-1 text-[11px] font-medium leading-tight',
    'transition-colors duration-[var(--pa-dur-fast)]',
    isActive ? 'bg-brand-soft text-ink' : 'text-ink-2 hover:bg-canvas hover:text-ink',
  ].join(' ');

interface Destination {
  to: string;
  label: string;
  /** Icon renderer; receives the active state so the TWO mark can dim its first circle. */
  icon: (active: boolean, size: 20 | 22) => ReactNode;
}

/**
 * App frame. Auth routes render bare (they bring their own AuthLayout). Signed-in screens get
 * three destinations: TWO, Tasks, You. A bottom tab bar under 768px, a compact rail from 768,
 * the full rail from 1024 (see `.app-shell` in index.css for the layout, M5.2).
 */
export function AppShell() {
  const { me } = useSession();
  const { pathname } = useLocation();
  const isAuthRoute = AUTH_ROUTES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  if (isAuthRoute || !me) return <Outlet />;
  return (
    <AgentProvider>
      <Frame />
    </AgentProvider>
  );
}

/**
 * The shell is one explicit height chain (html → body → #root → .app-shell, 100dvh) and, on
 * touch devices, follows the visual viewport (lib/viewport.ts) so the header stays at the top
 * and the composer at the bottom edge while the keyboard is open. The content region fills the
 * remaining width and the full height and is the only thing that scrolls (through the page's
 * own scroller); the reading column is centred inside it, 800px for the conversation and
 * 960px for Tasks and You.
 */
function Frame() {
  const { t } = useTranslation();
  const { me } = useSession();
  const { liveState, unseen: unseenMessages, pendingApprovals } = useAgent();
  // The attention dot: proactive messages that waited, or a card still waiting for a decision.
  const unseen = unseenMessages + pendingApprovals;
  const { pathname } = useLocation();
  useVisualViewport();
  // Every route change: no keyboard offset left behind, document at the top.
  useEffect(() => {
    resetViewport();
  }, [pathname]);
  const firstName = displayFirstName(me?.first_name) || me?.display_name || me?.email || '';
  const avatar = initials(me?.first_name ?? me?.display_name, me?.last_name);
  const stateLabel = t(`two.live.${liveState}`);
  const conversation = pathname === '/two' || pathname.startsWith('/two/');

  const destinations: Destination[] = [
    {
      to: '/two',
      label: t('nav.two'),
      // The second self is the destination: the static mark, not a speech bubble. Its second
      // circle stays TWO Blue in every state; only the first circle and the label dim. A dot
      // when TWO said something while the person was elsewhere (a reminder, a match, the brief).
      icon: (active) => (
        <span className="relative inline-flex">
          <BrandMark size={16} tone={active ? 'brand' : 'muted'} />
          {unseen > 0 ? (
            <span
              role="status"
              aria-label={t('two.unseenDot')}
              className="absolute -end-1.5 -top-1 size-2 rounded-full bg-brand"
            />
          ) : null}
        </span>
      ),
    },
    { to: '/tasks', label: t('nav.tasks'), icon: (_a, size) => <TasksIcon size={size} /> },
    { to: '/you', label: t('nav.you'), icon: (_a, size) => <Person size={size} /> },
  ];

  const accountRow = (compact: boolean) => (
    <NavLink
      to="/you/account"
      aria-label={t('you.account')}
      data-account-row
      className={[
        'mt-auto flex min-h-12 items-center rounded-md text-sm text-ink hover:bg-canvas',
        compact ? 'w-full justify-center px-1' : 'gap-3 px-2',
      ].join(' ')}
    >
      <span
        aria-hidden
        className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-soft text-sm font-semibold text-ink"
      >
        {avatar}
      </span>
      {compact ? null : (
        <span className="truncate" dir="auto">
          {firstName}
        </span>
      )}
    </NavLink>
  );

  return (
    <div className="app-shell bg-canvas">
      <VvDebug />

      {/* Full rail: 1024px and up. Sticky, full height, the lockup at the top, the account at the bottom. */}
      <nav
        aria-label={t('nav.primary')}
        data-rail="full"
        className="app-rail app-rail-full flex-col gap-1 border-e border-line bg-surface p-4"
      >
        <div className="mb-5 flex items-center px-2">
          <Lockup state={liveState} title={stateLabel} />
        </div>
        {destinations.map(({ to, label, icon }) => (
          <NavLink key={to} to={to} className={railLink}>
            {({ isActive }) => (
              <>
                <span className="flex w-5 items-center justify-center">{icon(isActive, 20)}</span>
                {label}
              </>
            )}
          </NavLink>
        ))}
        {accountRow(false)}
      </nav>

      {/* Compact rail: 768 to 1023. Icons with labels beneath, no tab bar. */}
      <nav
        aria-label={t('nav.primary')}
        data-rail="compact"
        className="app-rail app-rail-compact flex-col items-center gap-1 border-e border-line bg-surface px-1.5 py-3"
      >
        <div className="mb-4 flex justify-center">
          <Lockup state={liveState} title={stateLabel} size="sm" className="flex-col gap-1" />
        </div>
        {destinations.map(({ to, label, icon }) => (
          <NavLink key={to} to={to} className={compactLink}>
            {({ isActive }) => (
              <>
                <span className="flex h-6 items-center justify-center">{icon(isActive, 22)}</span>
                <span className="truncate">{label}</span>
              </>
            )}
          </NavLink>
        ))}
        {accountRow(true)}
      </nav>

      <div className="app-region flex min-h-0 min-w-0 flex-1 flex-col">
        {/* Mobile header: under 768 only. The lockup, the attention dot and the first name. */}
        <header className="app-mobile-header flex shrink-0 items-center gap-3 border-b border-line bg-surface px-4 pt-safe pb-3 pt-3">
          <Lockup state={liveState} title={stateLabel} />
          {unseen > 0 ? (
            <span
              role="status"
              aria-label={t('two.unseenDot')}
              className="size-2 shrink-0 rounded-full bg-brand"
            />
          ) : null}
          <span className="truncate text-sm text-ink-2" dir="auto">
            {firstName}
          </span>
        </header>

        <main
          data-column={conversation ? 'conversation' : 'page'}
          className={[
            'app-column mx-auto flex min-h-0 w-full flex-1 flex-col',
            conversation ? 'max-w-[800px]' : 'max-w-[960px]',
          ].join(' ')}
        >
          <Outlet />
        </main>

        {/* Bottom bar: under 768 only. Slides away while the composer is focused. */}
        <div className="bottom-nav-slot shrink-0">
          <div>
            <nav
              aria-label={t('nav.primary')}
              data-tabbar
              className="bottom-nav flex border-t border-line bg-surface px-2 pb-safe pt-1"
            >
              {destinations.map(({ to, label, icon }) => (
                <NavLink key={to} to={to} className={bottomLink}>
                  {({ isActive }) => (
                    <>
                      <span className="flex h-6 items-center justify-center">
                        {icon(isActive, 22)}
                      </span>
                      {label}
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}
